import { useCallback, useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { BillOut } from "../../../../../api/bills";
import {
  refreshBills,
  useChangeKitchenHold,
  useReleaseBillElement,
} from "../../../../../hooks/useBills";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import { useAppSelector } from "../../../../../store/hooks";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useOrderingMessage } from "../../useOrderingMessage";
import { useClosingBills } from "../payment/closingBills";
import type { KitchenHoldTime } from "../../menu/kitchenHoldItems";

/**
 * „Пусни сега" и „Промени" за задържаните партиди на показаната сметка
 * (`OrderDetails`). „Промени" отваря до бутона прозорче със седемте избора
 * от „Преглед на поръчката": „Сега" е „Пусни сега", минутите — влиза сама
 * след толкова минути ОТ СЕГА, ∞ (ръчно) — до „Пусни сега".
 *
 * Кой може: който може да добавя в сметката („Добави") — право `open_bill` и
 * сметка, която вижда (BE: `require_bill_access`); не и закрита или чакаща
 * принтера. В локален режим — заключено с „Недостъпно без интернет" (касата
 * няма пътищата, при влизане в локален режим тя сама пуска всичко задържано).
 *
 * Пускането е идемпотентно: вече освободена (таймерът, друг терминал) BE
 * връща 200 със същия документ — тогава само опресняването, без съобщение;
 * изчезнала партида (404) — също. Смяна на време на вече пусната — 409 с
 * текста на BE и опресняване.
 */
export const useKitchenHoldActions = (bill: BillOut | null) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const queryClient = useQueryClient();
  const releaseMutation = useReleaseBillElement();
  const changeMutation = useChangeKitchenHold();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const currentUserId = useAppSelector((state) => state.auth.userId);
  const edgeLocal = useIsEdgeLocal();
  const { closingBillIds } = useClosingBills();
  // Една заявка наведнъж — второ натискане (или друга партида) чака изхода;
  // `busyAction` — коя, за спинера на нейния бутон.
  const [busyElementId, setBusyElementId] = useState<number | null>(null);
  const [busyAction, setBusyAction] = useState<"release" | "change" | null>(null);
  // Партидата с отворено прозорче „Промени"; най-много една.
  const [changingElementId, setChangingElementId] = useState<number | null>(null);

  const canAct =
    permissions.some((permission) => permission.name === "open_bill") &&
    bill?.status === "open" &&
    !closingBillIds.has(bill.id);
  const lockedTooltip = edgeLocal ? t("edge.unavailableOffline") : undefined;

  const run = async (
    elementId: number,
    action: "release" | "change",
    request: () => Promise<void>,
    errorKey: string,
  ) => {
    if (!bill || !canAct || lockedTooltip || busyElementId !== null) return;
    setBusyElementId(elementId);
    setBusyAction(action);
    try {
      await request();
    } catch (error) {
      void refreshBills(queryClient);
      if (axios.isAxiosError(error) && error.response?.status === 404) return;
      // 409 закрита сметка / вече в кухнята, 423 облакът е в локален режим — текстът на BE.
      message.error(getApiErrorMessage(error, t(errorKey)));
    } finally {
      setBusyElementId(null);
      setBusyAction(null);
    }
  };

  const release = (elementId: number) =>
    run(
      elementId,
      "release",
      async () => {
        const updated = await releaseMutation.mutateAsync({ billId: bill!.id, elementId });
        const hold = updated.elements.find((element) => element.id === elementId)?.kitchen_hold;
        // Пусната от ТОВА натискане → потвърждение; иначе я е пуснал някой друг
        // (таймерът, колега) и екранът просто се опреснява.
        if (hold?.release_reason === "manual" && hold.released_by === currentUserId) {
          message.success(t("ordering.kitchenHold.released"));
        }
      },
      "ordering.kitchenHold.releaseError",
    );

  /** Изборът от „Промени" — изборът затваря прозорчето. */
  const pickTime = (elementId: number, time: KitchenHoldTime) => {
    setChangingElementId(null);
    if (time === "now") return release(elementId);
    return run(
      elementId,
      "change",
      async () => {
        await changeMutation.mutateAsync({
          billId: bill!.id,
          elementId,
          minutes: time === "manual" ? null : time,
        });
      },
      "ordering.kitchenHold.changeError",
    );
  };

  const toggleChanging = (elementId: number) =>
    setChangingElementId((current) => (current === elementId ? null : elementId));
  // Стабилна — Esc слушателят на прозорчето зависи от нея.
  const closeChanging = useCallback(() => setChangingElementId(null), []);

  return {
    canAct,
    lockedTooltip,
    busyElementId,
    busyAction,
    changingElementId,
    release,
    pickTime,
    toggleChanging,
    closeChanging,
  };
};
